/** @jsxImportSource @emotion/react */
import { ClassNames, css } from "@emotion/react";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, IconButton } from "@mui/material";
import { ReactNode } from "react";

import { AppTheme } from "@rewards-web/shared/style/types";

import { StylableProps } from "../../types";
import { Typography } from "../typography";

interface ModalProps extends StylableProps {
  open: boolean;
  onClose?(): void;
  onExited?(): void;
  children: ReactNode;
  width?: string;
  minHeight?: string;
  disableBackdropClick?: boolean;
  disablePadding?: boolean;
  background?: "default" | "white";
  showCloseButton?: boolean;
  disableCloseButton?: boolean;
  title?: ReactNode;
}

/**
 * Sizes the 1em FontAwesome close icon to 24px ("large" alone would make it 28px).
 */
function v4IconButtonFontSize(theme: AppTheme) {
  return css`
    font-size: ${theme.typography.pxToRem(24)};
  `;
}

export function Modal({
  className,
  width,
  open,
  onClose,
  onExited,
  children,
  disableBackdropClick,
  minHeight,
  disablePadding,
  background = "default",
  showCloseButton = false,
  disableCloseButton = false,
  title,
}: ModalProps) {
  const maybeCloseButton = showCloseButton && (
    <IconButton
      aria-label="close"
      onClick={() => onClose?.()}
      disabled={disableCloseButton}
      // "large" for its 12px padding
      size="large"
      css={(theme: AppTheme) => css`
        ${v4IconButtonFontSize(theme)}
        position: absolute;
        right: 8px;
        top: 8px;
        z-index: 1;
        color: ${theme.palette.grey[500]};

        &.Mui-disabled {
          color: ${theme.palette.grey[300]};
        }
      `}
    >
      <FontAwesomeIcon icon={faClose} width={24} height={24} />
    </IconButton>
  );
  return (
    <ClassNames>
      {({ css, theme }) => (
        <Dialog
          classes={{
            paper: css`
              background-color: ${
                background === "default" ? "#f9f9fb" : "#ffffff"
              };
              padding: ${disablePadding ? 0 : theme.spacing(3, 2)};
              width: ${width};
              max-width: initial;
              ${
                minHeight &&
                `${css`
                  min-height: ${minHeight};
                `}`
              }
              border-radius: 20px;
            `,
          }}
          className={className}
          open={open}
          maxWidth="xs"
          onClose={(_, reason) => {
            if (disableBackdropClick && reason === "backdropClick") {
              return; // not disabling backdrop click
            }

            onClose?.();
          }}
          slotProps={{
            transition: {
              onExited,
            },
          }}
        >
          {title && (
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding-left: ${theme.spacing(3)};
              `}
            >
              <Typography variant="h5">{title}</Typography>
              <IconButton
                aria-label="close"
                onClick={() => onClose?.()}
                // "large" for its 12px padding
                size="large"
                css={v4IconButtonFontSize(theme)}
              >
                <FontAwesomeIcon icon={faClose} width={24} height={24} />
              </IconButton>
            </div>
          )}
          {!title && maybeCloseButton}
          {children}
        </Dialog>
      )}
    </ClassNames>
  );
}
